import type { TeddyKey } from '../teddies.js'
import { cluesFor, pictureFrom, type Clues, type MosaicSize, type Picture } from './mosaic-solve.js'

/**
 * The pictures Maritza's Mosaic is made of.
 *
 * **Drawn by hand, and that is the point.** A generated grid is noise, and
 * noise cannot be recognised — which would cost the game the only thing it has
 * that the others do not. Every game here tells her how a round went in the
 * round's own terms; this one hands her something she made by reasoning, and a
 * picture nobody can name is just a filled-in grid.
 *
 * So the set is finite and small, and that is fine: the deduction is in the
 * solving, and she will not solve a 15×15 twice from memory.
 *
 * **Written as text** so that a diff shows the teddy rather than four hundred
 * booleans, and so that redrawing one is redrawing it. `pictureFrom` refuses a
 * row of the wrong width or a character that is neither `#` nor `.`, because
 * the alternative is a square silently left empty and a picture that no longer
 * means what it looks like here.
 *
 * **Nothing in this file asserts a picture is fair.** Every one is checked by
 * `mosaic-pictures.test.ts` against the solver, by name, and a redrawn picture
 * that gains a second solution fails the suite saying which one it was. A
 * puzzle that vouches for itself is a puzzle agreeing with itself while being
 * wrong — the rule Rocky's Riddles and Stripey's Sudoku are already built on.
 */
export interface MosaicPicture {
  /** Stable, and used as the round's difficulty label where a play is recorded. */
  readonly key: string
  /** What it turns out to be, said to her when it does. */
  readonly title: string
  /**
   * The teddy this is, or null where it is a thing rather than somebody.
   *
   * Five across is twenty-five squares, which is a heart or a fish and cannot
   * be anybody in particular. Rather than ship a teddy nobody would recognise,
   * the small grids are objects from her teddies' world and a teddy starts at
   * ten — so the promise of a teddy at the end is only made where it can be
   * kept.
   */
  readonly teddy: TeddyKey | null
  readonly size: MosaicSize
  /** `#` filled, `.` blank, one string per row. */
  readonly rows: readonly string[]
}

export const MOSAIC_PICTURES: readonly MosaicPicture[] = [
  {
    key: 'heart',
    title: 'A heart',
    teddy: null,
    size: 5,
    rows: ['.#.#.', '#####', '#####', '.###.', '..#..'],
  },
  {
    key: 'star',
    title: 'A star',
    teddy: null,
    size: 5,
    rows: ['..#..', '#####', '.###.', '.#.#.', '#...#'],
  },
  {
    key: 'house',
    title: 'A house',
    teddy: null,
    size: 5,
    rows: ['..#..', '.###.', '#####', '##.##', '##.##'],
  },
  {
    key: 'tree',
    title: 'A tree',
    teddy: null,
    size: 5,
    rows: ['..#..', '.###.', '#####', '..#..', '..#..'],
  },
  {
    key: 'mickey-face',
    title: 'Mickey',
    teddy: 'mickey',
    size: 10,
    rows: [
      '###....###',
      '###....###',
      '.##....##.',
      '..######..',
      '.########.',
      '##########',
      '##########',
      '.########.',
      '..######..',
      '....##....',
    ],
  },
  {
    key: 'hoppity-face',
    title: 'Hoppity',
    teddy: 'hoppity',
    size: 10,
    rows: [
      '.##....##.',
      '.##....##.',
      '.##....##.',
      '..#....#..',
      '..######..',
      '.########.',
      '##.####.##',
      '##########',
      '.########.',
      '..######..',
    ],
  },
  {
    key: 'sky-face',
    title: 'Sky',
    teddy: 'sky',
    size: 10,
    rows: [
      '..######..',
      '.########.',
      '##########',
      '##.####.##',
      '##########',
      '.########.',
      '..######..',
      '#.#.##.#.#',
      '#.#.##.#.#',
      '#.#.##.#.#',
    ],
  },
  {
    key: 'mickey-whole',
    title: 'Mickey',
    teddy: 'mickey',
    size: 15,
    rows: [
      '###.........###',
      '####.......####',
      '####.......####',
      '###.........###',
      '....#######....',
      '...#########...',
      '..###########..',
      '..##.#####.##..',
      '..###########..',
      '...#########...',
      '....#######....',
      '...#########...',
      '..###.###.###..',
      '..###.###.###..',
      '.###...#...###.',
    ],
  },
  {
    key: 'hoppity-whole',
    title: 'Hoppity',
    teddy: 'hoppity',
    size: 15,
    rows: [
      '..##.......##..',
      '..##.......##..',
      '..##.......##..',
      '..##.......##..',
      '...##.....##...',
      '....#######....',
      '...#########...',
      '..##.#####.##..',
      '...#########...',
      '....#######....',
      '...#########...',
      '..###########..',
      '..###########..',
      '...##.....##...',
      '..###.....###..',
    ],
  },
  {
    key: 'fish',
    title: 'A fish',
    teddy: null,
    size: 5,
    rows: ['...##', '.####', '#####', '.####', '...##'],
  },
  {
    key: 'moon',
    title: 'The moon',
    teddy: null,
    size: 5,
    rows: ['.###.', '##...', '##...', '##...', '.###.'],
  },
  {
    key: 'boat',
    title: 'A boat',
    teddy: null,
    size: 5,
    rows: ['..##.', '..###', '..##.', '#####', '.###.'],
  },
  {
    key: 'flower',
    title: 'A flower',
    teddy: null,
    size: 5,
    rows: ['.###.', '#####', '.###.', '..#..', '..#..'],
  },
  {
    key: 'key',
    title: 'A key',
    teddy: null,
    size: 5,
    rows: ['.###.', '.#.#.', '.###.', '..#..', '..##.'],
  },
  {
    key: 'apple',
    title: 'An apple',
    teddy: null,
    size: 5,
    rows: ['..#..', '.###.', '#####', '#####', '.###.'],
  },
  {
    key: 'cup',
    title: 'A cup',
    teddy: null,
    size: 5,
    rows: ['#####', '#...#', '#...#', '.###.', '..#..'],
  },
  {
    key: 'bone',
    title: 'A bone',
    teddy: null,
    size: 5,
    rows: ['##.##', '#####', '.###.', '#####', '##.##'],
  },
  {
    key: 'plus',
    title: 'A plus sign',
    teddy: null,
    size: 5,
    rows: ['..#..', '..#..', '#####', '..#..', '..#..'],
  },
  {
    key: 'letter',
    title: 'A letter',
    teddy: null,
    size: 5,
    rows: ['#####', '##.##', '#.#.#', '#...#', '#####'],
  },
  {
    key: 'rudolf-face',
    title: 'Rudolf',
    teddy: 'rudolf',
    size: 10,
    rows: [
      '#.#....#.#',
      '.###..###.',
      '..#....#..',
      '..######..',
      '.########.',
      '##########',
      '##.####.##',
      '.########.',
      '..######..',
      '....##....',
    ],
  },
  {
    key: 'turtley-face',
    title: 'Turtley',
    teddy: 'turtley',
    size: 10,
    rows: [
      '....##....',
      '..######..',
      '.########.',
      '##########',
      '##.####.##',
      '##########',
      '.########.',
      '..######..',
      '.##....##.',
      '##......##',
    ],
  },
  {
    key: 'rocky-face',
    title: 'Rocky',
    teddy: 'rocky',
    size: 10,
    rows: [
      '##......##',
      '###....###',
      '##########',
      '##########',
      '##.####.##',
      '##########',
      '.########.',
      '..######..',
      '...####...',
      '....##....',
    ],
  },
  {
    key: 'nemo-face',
    title: 'Nemo',
    teddy: 'nemo',
    size: 10,
    rows: [
      '.......##.',
      '##....####',
      '###..#####',
      '##########',
      '####.#####',
      '####.#####',
      '##########',
      '###..#####',
      '##....####',
      '.......##.',
    ],
  },
  {
    key: 'chaka-face',
    title: 'Chaka',
    teddy: 'chaka',
    size: 10,
    rows: [
      '...##.....',
      '..####....',
      '..######..',
      '.#######..',
      '###.####..',
      '.#######..',
      '..######..',
      '...####...',
      '...####...',
      '..##..##..',
    ],
  },
  {
    key: 'tigger-face',
    title: 'Tigger',
    teddy: 'tigger',
    size: 10,
    rows: [
      '##......##',
      '####..####',
      '##########',
      '##.####.##',
      '##########',
      '##########',
      '#.######.#',
      '.########.',
      '..######..',
      '...####...',
    ],
  },
  {
    key: 'turtley-whole',
    title: 'Turtley',
    teddy: 'turtley',
    size: 15,
    rows: [
      '......###......',
      '.....#####.....',
      '.....##.##.....',
      '......###......',
      '...#########...',
      '..###########..',
      '.#############.',
      '###############',
      '###.#######.###',
      '###############',
      '.#############.',
      '..###########..',
      '...#########...',
      '##...........##',
      '###.........###',
    ],
  },
  {
    key: 'rudolf-whole',
    title: 'Rudolf',
    teddy: 'rudolf',
    size: 15,
    rows: [
      '##.#.......#.##',
      '.####.....####.',
      '..##.......##..',
      '....#######....',
      '...#########...',
      '...##.###.##...',
      '...#########...',
      '....#######....',
      '......###......',
      '..###########..',
      '.#############.',
      '.#############.',
      '.##.##...##.##.',
      '.##.##...##.##.',
      '.##.##...##.##.',
    ],
  },
  {
    /*
     * The game's own teddy, which it ought to have.
     *
     * `-face` and `-whole` are size markers rather than descriptions — a
     * cactus has no face to draw, and neither does Nemo at ten across. The
     * suffix says which of the two teddy sizes a picture is, and that is all
     * it has ever said.
     */
    key: 'maritza-face',
    title: 'Maritza',
    teddy: 'maritza',
    size: 10,
    rows: [
      '...##.....',
      '...##.....',
      '#..##..#..',
      '#..##..#..',
      '#####..#..',
      '...#####..',
      '...##.....',
      '...##.....',
      '...##.....',
      '..####....',
    ],
  },
  {
    key: 'maritza-whole',
    title: 'Maritza',
    teddy: 'maritza',
    size: 15,
    rows: [
      '......###......',
      '......###......',
      '......###......',
      '......###..##..',
      '..##..###..##..',
      '..##..###..##..',
      '..##..###..##..',
      '..##..#######..',
      '..#######......',
      '......###......',
      '......###......',
      '......###......',
      '......###......',
      '.....#####.....',
      '....#######....',
    ],
  },
]

/** The squares this picture is, read from the rows it is drawn as. */
export function squaresOf(picture: MosaicPicture): Picture {
  return pictureFrom(picture.rows).picture
}

/** The numbers she works from. */
export function cluesOf(picture: MosaicPicture): Clues {
  return cluesFor(squaresOf(picture), picture.size)
}

/** Every picture at this size, which is what a round is dealt from. */
export function picturesOfSize(size: MosaicSize): readonly MosaicPicture[] {
  return MOSAIC_PICTURES.filter((picture) => picture.size === size)
}

/**
 * One picture by key.
 *
 * Throws rather than returning undefined: a key that names no picture is a
 * round issued against a set that has changed under it, and drawing an empty
 * grid in front of her is a worse answer than stopping.
 */
export function pictureByKey(key: string): MosaicPicture {
  const found = MOSAIC_PICTURES.find((picture) => picture.key === key)

  if (!found) throw new Error(`No mosaic picture called ${key}`)

  return found
}
